import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Account, AccountUserMeta } from 'entities/account/model/types';
import { EMI } from 'shared/model/types';
import { PURGE } from 'redux-persist';
import { RootState } from 'shared/config/store/store';

type AccountState = {
  accounts: Record<string, Account>;
  meta: AccountUserMeta | Record<string, never>;
  currentAccount: number | null;
  isSoftBlocked: boolean;
  monthSpend: Record<string, number>;
};

type AccountReducer = {
  [EMI.Wallester]: AccountState;
};

const initialState: AccountReducer = {
  [EMI.Wallester]: {
    accounts: {},
    meta: {},
    isSoftBlocked: false,
    currentAccount: null,
    monthSpend: {}
  }
};

const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    setCurrentAccount(state, action: PayloadAction<{ emi: EMI; accountId: number }>) {
      const { emi, accountId } = action.payload;
      state[emi].currentAccount = accountId;
    },
    setAccounts(state, action: PayloadAction<{ emi: EMI; accounts: Account[] }>) {
      const { emi, accounts } = action.payload;
      const mappedAccounts = accounts.reduce((acc: Record<string, Account>, current: Account) => {
        acc[current.id] = { ...current };
        return acc;
      }, {});
      state[emi].accounts = mappedAccounts;
    },
    refreshAccounts(state, action: PayloadAction<{ emi: EMI; accounts: Account[] }>) {
      const { emi, accounts } = action.payload;
      const mappedAccounts = accounts.reduce((acc: Record<string, Account>, current: Account) => {
        acc[current.id] = { ...current };
        return acc;
      }, {});
      state[emi].accounts = mappedAccounts;
    },
    addAccounts(state, action: PayloadAction<{ emi: EMI; accounts: Account[] }>) {
      const { emi, accounts } = action.payload;
      const newAccountsObj = accounts.reduce((acc: Record<string, Account>, current: Account) => {
        acc[current.id] = { ...current };
        return acc;
      }, {});
      state[emi].accounts = {
        ...state[emi].accounts,
        ...newAccountsObj
      };
    },
    setMonthSpend(
      state,
      action: PayloadAction<{ emi: EMI; accountId: number; monthSpend: number }>
    ) {
      const { emi, accountId, monthSpend } = action.payload;
      state[emi].monthSpend[accountId] = monthSpend;
    },
    setAccountTitle(state, action: PayloadAction<{ emi: EMI; accountId: number; title: string }>) {
      const { emi, accountId, title } = action.payload;
      state[emi].accounts[accountId] = {
        ...state[emi].accounts[accountId],
        title
      };
    },
    setMeta(state, action: PayloadAction<{ emi: EMI; meta: Partial<AccountUserMeta> }>) {
      const { emi, meta } = action.payload;

      // Проверяем, есть ли уже meta, если нет — создаем пустой объект
      if (!state[emi].meta || Object.keys(state[emi].meta).length === 0) {
        state[emi].meta = { ...meta } as AccountUserMeta;
      } else {
        Object.assign(state[emi].meta, meta);
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  }
});

export const {
  setCurrentAccount,
  setAccounts,
  refreshAccounts,
  addAccounts,
  setAccountTitle,
  setMeta,
  setMonthSpend
} = accountsSlice.actions;

export default accountsSlice.reducer;

const selectAccountStateByPlan = (state: RootState, emi: EMI) => state.accounts[emi];

export const getEMIAccounts = createSelector(
  [selectAccountStateByPlan],
  (accountState) => accountState.accounts
);
export const getEMICurrentAccount = createSelector(
  [selectAccountStateByPlan],
  (accountState) => accountState.currentAccount
);
export const getEMICurrentAccountObject = createSelector(
  [selectAccountStateByPlan],
  (accountState) =>
    accountState.currentAccount && accountState.accounts[accountState.currentAccount]
);
export const getEMIAccountMeta = createSelector(
  [selectAccountStateByPlan],
  (accountState) => accountState.meta
);
export const getEMIBlockStatus = createSelector(
  [selectAccountStateByPlan],
  (accountState) => accountState.isSoftBlocked
);
export const getEMIMonthSpend = createSelector(
  [selectAccountStateByPlan],
  (accountState) => accountState.monthSpend
);
